import {Async, Loaded, Loading} from 'Core/models/Async';
import {StringUnion} from 'Core/models/EmptyString';

export class UnassignedFormNumber {
  public static create(formNumber: StringUnion) {
    return new UnassignedFormNumber(formNumber);
  }

  public readonly key = 'UnassignedFormNumber';
  constructor(public readonly value: StringUnion) {}
}

export class UserAssignedFormNumber {
  public static create(
    formNumber: string,
    documentShortName: string,
    fipscoDocumentName: string,
    documentLongName: string,
    documentName: string
  ) {
    return new UserAssignedFormNumber(
      formNumber,
      documentShortName,
      fipscoDocumentName,
      documentLongName,
      documentName
    );
  }

  public readonly key = 'UserAssignedFormNumber';
  constructor(
    public readonly value: string,
    public readonly documentShortName: string,
    public readonly fipscoDocumentName: string,
    public readonly documentLongName: string,
    public readonly documentName: string
  ) {}
}

export class FormNumberError {
  public static create(formNumber: string) {
    return new FormNumberError(formNumber);
  }

  public readonly key = 'FormNumberError';
  constructor(public readonly value: string) {}
}

export type FormNumber =
  | Async<UserAssignedFormNumber>
  | UnassignedFormNumber
  | UserAssignedFormNumber
  | FormNumberError;

type MatchFormNumberOptions<TReturn> = {
  Loading: () => TReturn;
  Loaded: (formNumber: UserAssignedFormNumber) => TReturn;
  Error: () => TReturn;
  Unassigned: (formNumber: StringUnion) => TReturn;
  UserAssignedFormNumber: (
    formNumber: string,
    documentShortName: string,
    fipscoDocumentName: string,
    documentLongName: string,
    documentName: string
  ) => TReturn;
  FormNumberError: (formNumber: string) => TReturn;
};

export const isLoading = (formNumber: FormNumber): formNumber is Loading<UserAssignedFormNumber> =>
  formNumber.key === 'Loading';

export const isLoaded = (formNumber: FormNumber): formNumber is Loaded<UserAssignedFormNumber> =>
  formNumber.key === 'Loaded';

export const isFormNumberError = (formNumber: FormNumber): formNumber is FormNumberError =>
  formNumber.key === 'FormNumberError';

export const isUserAssignedFormNumber = (formNumber: FormNumber): formNumber is UserAssignedFormNumber =>
  formNumber.key === 'UserAssignedFormNumber';

export const matchFormNumber = <TReturn>(options: MatchFormNumberOptions<TReturn>) => (formNumber: FormNumber) => {
  switch (formNumber.key) {
    case 'UnassignedFormNumber':
      return options.Unassigned(formNumber.value);
    case 'Loaded':
      return options.Loaded(formNumber.Data);
    case 'Loading':
      return options.Loading();
    case 'Conflict':
      return options.Error();
    case 'ServerError':
      return options.Error();
    case 'Unauthorized':
      return options.Error();
    case 'UserAssignedFormNumber':
      return options.UserAssignedFormNumber(
        formNumber.value,
        formNumber.documentShortName,
        formNumber.fipscoDocumentName,
        formNumber.documentLongName,
        formNumber.documentName
      );
    case 'FormNumberError':
      return options.FormNumberError(formNumber.value);
  }
};
